import React, { useMemo } from 'react';
import { useTheme } from '../../../contexts/ThemeContext';

/** Deterministic bar heights (percentage of container) — varied, not uniform. */
const BAR_HEIGHTS = [60, 85, 42, 92, 50, 78, 38, 70];

/** Light-mode grey shades per bar — lighter palette matching #EAEAEA base. */
const BAR_COLORS_LIGHT = [
  '#E0E0E0', '#EAEAEA', '#D8D8D8', '#E4E4E4',
  '#DBDBDB', '#E7E7E7', '#DDDDDD', '#E2E2E2',
];

/** Dark-mode grey shades per bar. */
const BAR_COLORS_DARK = [
  '#4b5563', '#374151', '#525d6e', '#3f4a5a',
  '#5a6577', '#414c5c', '#4e596a', '#3a4555',
];

/**
 * Deterministic animation parameters per bar.
 * Delay and duration vary by index so bars don't move in sync.
 */
function getBarStyle(index: number, isDark: boolean): React.CSSProperties {
  const delay = ((index * 137 + 29) % 80) / 100;
  const duration = 1.2 + ((index * 53 + 17) % 60) / 100;
  const colors = isDark ? BAR_COLORS_DARK : BAR_COLORS_LIGHT;

  return {
    '--bar-delay': `${delay}s`,
    '--bar-duration': `${duration}s`,
    height: `${BAR_HEIGHTS[index]}%`,
    background: colors[index % colors.length],
  } as React.CSSProperties;
}

/**
 * Bar chart skeleton — 8 bars with varying heights, aligned to bottom.
 *
 * Each bar has a unique color, delay, and duration to prevent synchronized motion.
 * Animation: scaleY(0.3) → scaleY(1), alternate infinite.
 * Bottom padding simulates X-axis space.
 */
interface BarChartSkeletonProps {
  /** Real chart title from plan preview — replaces shimmer placeholder. */
  title?: string;
}

const BarChartSkeleton: React.FC<BarChartSkeletonProps> = ({ title }) => {
  const { isDarkMode } = useTheme();

  const bars = useMemo(
    () => BAR_HEIGHTS.map((_, i) => getBarStyle(i, isDarkMode)),
    [isDarkMode],
  );

  return (
    <div className="skeleton-chart-container">
      <div className="bar-skeleton">
        {title ? (
          <div className="chart-skeleton__real-title">{title}</div>
        ) : (
          <div className="skeleton-block bar-skeleton__title" />
        )}

        <div className="bar-skeleton__chart">
          {bars.map((style, i) => (
            <div key={i} className="bar-skeleton__bar" style={style} />
          ))}
        </div>

        <div className="bar-skeleton__axis" />
      </div>
    </div>
  );
};

export default React.memo(BarChartSkeleton);